import { forwardRef } from 'react';
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';

/**
 * Input Component - A versatile text input with validation states
 * 
 * @param {Object} props - Component props
 * @param {string} props.type - Input type: 'text', 'email', 'password', 'number', etc.
 * @param {string} props.label - Input label
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.hint - Helper text below input
 * @param {string} props.error - Error message
 * @param {boolean} props.success - Shows success state
 * @param {string} props.size - Input size: 'sm', 'md', 'lg'
 * @param {boolean} props.disabled - Disables the input
 * @param {React.ReactNode} props.leftIcon - Icon to display on the left
 * @param {React.ReactNode} props.rightIcon - Icon to display on the right
 * @param {string} props.className - Additional CSS classes
 */
const Input = forwardRef(({
  type = 'text',
  label,
  placeholder,
  hint,
  error,
  success,
  size = 'md',
  disabled = false,
  leftIcon,
  rightIcon,
  className = '',
  id,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-3 text-base',
  };

  // State classes
  const getStateClasses = () => {
    if (error) return 'border-error focus:ring-error focus:border-error';
    if (success) return 'border-success focus:ring-success focus:border-success';
    return 'border-gray-300 focus:ring-primary-500 focus:border-transparent';
  };

  const isPassword = type === 'password';

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label htmlFor={inputId} className="form-label">
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            {leftIcon}
          </div>
        )}
        <input
          ref={ref}
          id={inputId}
          type={isPassword && showPassword ? 'text' : type}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full rounded-lg transition-all duration-200 ease-in-out
            focus:outline-none focus:ring-2 focus:ring-offset-0
            disabled:bg-gray-100 disabled:cursor-not-allowed
            placeholder-gray-400
            ${sizeClasses[size]}
            ${getStateClasses()}
            ${leftIcon ? 'pl-10' : ''}
            ${(rightIcon || isPassword || error || success) ? 'pr-10' : ''}
          `.trim().replace(/\s+/g, ' ')}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={hint ? `${inputId}-hint` : error ? `${inputId}-error` : undefined}
          {...props}
        />
        
        {/* Password toggle */}
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        )}

        {/* Status icons */}
        {!isPassword && error && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <AlertCircle className="w-5 h-5 text-error" />
          </div>
        )}
        {!isPassword && success && !error && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <CheckCircle className="w-5 h-5 text-success" />
          </div>
        )}
        
        {/* Custom right icon */}
        {!isPassword && !error && !success && rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
            {rightIcon}
          </div>
        )}
      </div>
      
      {/* Hint text */}
      {hint && !error && (
        <p id={`${inputId}-hint`} className="form-hint">
          {hint}
        </p>
      )}
      
      {/* Error message */}
      {error && (
        <p id={`${inputId}-error`} className="form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

/**
 * Textarea Component - Multi-line text input
 */
export const Textarea = forwardRef(({
  label,
  placeholder,
  hint,
  error,
  success,
  rows = 4,
  disabled = false,
  className = '',
  id,
  ...props
}, ref) => {
  const inputId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

  const getStateClasses = () => {
    if (error) return 'border-error focus:ring-error focus:border-error';
    if (success) return 'border-success focus:ring-success focus:border-success';
    return 'border-gray-300 focus:ring-primary-500 focus:border-transparent';
  };

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label htmlFor={inputId} className="form-label">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={inputId}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={`
          w-full px-4 py-2 text-sm rounded-lg border transition-all duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-offset-0
          disabled:bg-gray-100 disabled:cursor-not-allowed
          placeholder-gray-400 resize-none
          ${getStateClasses()}
        `.trim().replace(/\s+/g, ' ')}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={hint ? `${inputId}-hint` : error ? `${inputId}-error` : undefined}
        {...props}
      />
      {hint && !error && (
        <p id={`${inputId}-hint`} className="form-hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${inputId}-error`} className="form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

export default Input;